import React, { useState, useEffect } from 'react';
import { 
  Eye, EyeOff, GripVertical, Plus, Trash2, Settings, 
  ChevronDown, ChevronUp, Loader2, Save, RotateCcw
} from 'lucide-react';
import { dossierConfigAPI } from '../../services/api';

/**
 * Widget de configuration de la fiche dossier
 * Permet de gérer les sections et champs affichés dans l'onglet Dossier
 */
const WidgetDossierConfig = () => {
  const [config, setConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSection, setNewSection] = useState({ code: '', nom: '', icone: '📋' });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const res = await dossierConfigAPI.getFullConfigAdmin();
      setConfig(res.data);
      
      // Si vide, initialiser avec les valeurs par défaut
      if (res.data.length === 0) {
        await dossierConfigAPI.initDefaults();
        const resInit = await dossierConfigAPI.getFullConfigAdmin();
        setConfig(resInit.data);
      }
    } catch (error) {
      console.error('Erreur chargement config:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSectionVisibility = async (section) => {
    setSaving(section.id);
    try {
      await dossierConfigAPI.updateSection(section.id, { visible: !section.visible });
      await loadConfig();
    } catch (error) {
      console.error('Erreur toggle section:', error);
    } finally {
      setSaving(null);
    }
  };

  const toggleFieldVisibility = async (field) => {
    setSaving(field.id);
    try {
      await dossierConfigAPI.updateField(field.id, { visible: !field.visible });
      await loadConfig();
    } catch (error) {
      console.error('Erreur toggle field:', error);
    } finally {
      setSaving(null);
    }
  };

  const handleAddSection = async (e) => {
    e.preventDefault();
    if (!newSection.code || !newSection.nom) return;
    
    setSaving('new');
    try {
      await dossierConfigAPI.createSection({
        code: newSection.code.toLowerCase().replace(/\s+/g, '_'),
        nom: newSection.nom,
        icone: newSection.icone,
        ordre: config.length,
        visible: true
      });
      await loadConfig();
      setNewSection({ code: '', nom: '', icone: '📋' });
      setShowAddSection(false);
    } catch (error) {
      console.error('Erreur création section:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(null);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Supprimer cette section et tous ses champs ?')) return;
    
    setSaving(sectionId);
    try {
      await dossierConfigAPI.deleteSection(sectionId);
      await loadConfig();
    } catch (error) {
      console.error('Erreur suppression section:', error);
    } finally {
      setSaving(null);
    }
  };

  const toggleExpand = (sectionCode) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionCode]: !prev[sectionCode]
    }));
  };

  const moveSection = async (index, direction) => {
    const newConfig = [...config];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= config.length) return;
    
    [newConfig[index], newConfig[newIndex]] = [newConfig[newIndex], newConfig[index]];
    setConfig(newConfig);
    
    // Sauvegarder le nouvel ordre
    const ids = newConfig.map(s => s.id);
    await dossierConfigAPI.reorderSections(ids);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const visibleSections = config.filter(s => s.visible).length;
  const totalFields = config.reduce((sum, s) => sum + s.fields.length, 0);
  const visibleFields = config.reduce((sum, s) => sum + s.fields.filter(f => f.visible).length, 0);

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="flex items-center gap-4 text-sm">
        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full">
          {visibleSections}/{config.length} sections visibles
        </span>
        <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
          {visibleFields}/{totalFields} champs visibles
        </span>
      </div>

      {/* Liste des sections */}
      <div className="space-y-3">
        {config.map((section, index) => (
          <div 
            key={section.id}
            className={`rounded-xl border-2 transition ${
              section.visible 
                ? 'border-gray-200 bg-white' 
                : 'border-gray-200 bg-gray-50 opacity-60'
            }`}
          >
            {/* Header de la section */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1">
                  <button 
                    onClick={() => moveSection(index, -1)}
                    disabled={index === 0}
                    className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => moveSection(index, 1)}
                    disabled={index === config.length - 1}
                    className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
                <span className="text-2xl">{section.icone}</span>
                <div>
                  <h3 className="font-semibold text-gray-900">{section.nom}</h3>
                  <span className="text-xs text-gray-500">
                    {section.fields.filter(f => f.visible).length}/{section.fields.length} champs visibles
                  </span>
                </div>
                {!section.visible && (
                  <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Masqué</span>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleSectionVisibility(section)}
                  disabled={saving === section.id}
                  className={`p-2 rounded-lg transition ${
                    section.visible 
                      ? 'text-green-600 hover:bg-green-50' 
                      : 'text-gray-400 hover:bg-gray-100'
                  }`}
                  title={section.visible ? 'Masquer la section' : 'Afficher la section'}
                >
                  {saving === section.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : section.visible ? (
                    <Eye className="w-5 h-5" />
                  ) : (
                    <EyeOff className="w-5 h-5" />
                  )}
                </button>
                <button
                  onClick={() => toggleExpand(section.code)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                >
                  {expandedSections[section.code] ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Champs de la section (expandable) */}
            {expandedSections[section.code] && (
              <div className="p-4 bg-gray-50">
                <div className="space-y-2">
                  {section.fields.map((field) => (
                    <div 
                      key={field.id}
                      className={`flex items-center justify-between p-3 rounded-lg transition ${
                        field.visible ? 'bg-white' : 'bg-gray-100 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-4 h-4 text-gray-300" />
                        <div>
                          <span className={`font-medium ${field.visible ? 'text-gray-900' : 'text-gray-500'}`}>
                            {field.label}
                          </span>
                          {field.obligatoire && (
                            <span className="ml-2 text-red-500 text-xs">*</span>
                          )}
                          <div className="text-xs text-gray-400">
                            {field.code} • {field.type_champ}
                            {field.largeur === 'col-span-2' && ' • Pleine largeur'}
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => toggleFieldVisibility(field)}
                        disabled={saving === field.id}
                        className={`p-1.5 rounded transition ${
                          field.visible 
                            ? 'text-green-600 hover:bg-green-50' 
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                      >
                        {saving === field.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : field.visible ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  ))}
                  
                  {section.fields.length === 0 && (
                    <p className="text-center text-gray-500 py-4">Aucun champ dans cette section</p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Bouton ajouter section */}
      {showAddSection ? (
        <form onSubmit={handleAddSection} className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <h4 className="font-semibold text-gray-900 mb-3">➕ Nouvelle section</h4>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Icône</label>
              <input
                type="text"
                value={newSection.icone}
                onChange={(e) => setNewSection({ ...newSection, icone: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-center text-xl"
                placeholder="📋"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
              <input
                type="text"
                value={newSection.code}
                onChange={(e) => setNewSection({ ...newSection, code: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                placeholder="ma_section"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
              <input
                type="text"
                value={newSection.nom}
                onChange={(e) => setNewSection({ ...newSection, nom: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                placeholder="Ma Section"
                required
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowAddSection(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving === 'new'}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              {saving === 'new' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Créer
            </button>
          </div>
        </form>
      ) : (
        <button
          onClick={() => setShowAddSection(true)}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-blue-400 hover:text-blue-600 transition flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Ajouter une section
        </button>
      )}
    </div>
  );
};

export default WidgetDossierConfig;
